import { ActiveStatus, MeetingStatus, Patient, PatientMeetingData, PatientSummary } from "../types";
import { batchFetchItems, getUrl } from "./service-utils";

const getPatientURL = (
    clientId: string | undefined,
    parameters?: string | undefined,
) => {

    return getUrl("patient", clientId, parameters)
}

export const dataToPatient = (data: any): Patient | null => {

    if (!data) {
        return null
    }

    if (!data.patientId) {
        console.warn("Tried to convert object without id", data)
        return null
    }

    const meetingData: PatientMeetingData[] = (data.meetingData || []).map(dataToMeetingData)

    const patient: Patient = {
        patientId: data.patientId,
        displayId: data.displayId || "",
        created: data.created ? new Date(data.created) : new Date(),
        status: data.status || ActiveStatus.Active,
        meetingIds: data.meetingIds || [],
        lastMeetingId: data.lastMeetingId,
        nextMeetingId: data.nextMeetingId,
        meetingData,
    };

    return patient
}

const dataToMeetingData = (item: any): PatientMeetingData => {
    const startTime = item?.startTime ? new Date(item?.startTime) : undefined

    return {
        meetingId: item?.meetingId || "",
        startTime,
        meetingType: item?.meetingType || MeetingStatus.Scheduled,
        creatorId: item?.creatorId || "",
    }
}

export const dataToPatientSummary = (data: any): PatientSummary | null => {

    if (!data) {
        return null
    }

    if (!data.patientId) {
        console.warn("Tried to convert object without id", data)
        return null
    }

    const patient: PatientSummary = {
        patientId: data.patientId,
        displayId: data.displayId || "",
        status: data.status || ActiveStatus.Active,
        created: data.created ? new Date(data.created) : new Date(),
        lastMeeting: data.lastMeeting ? dataToMeetingData(data.lastMeeting) : undefined,
        nextMeeting: data.nextMeeting ? dataToMeetingData(data.nextMeeting) : undefined,
    };

    return patient
}

/** 
 * Lists all patients
 * @param authToken - Bearer token for authentication
 * @param clientId - The id for the client partition
 * @returns Promise resolving to an array of Patient objects
*/
const listPatients = async (
    authToken: string,
    clientId?: string | undefined,
): Promise<PatientSummary[]> => {

    if (clientId != undefined && clientId.length == 0) {
        return []
    }

    try {

        const url = getPatientURL(clientId)

        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        const patients: Patient[] = data.map(dataToPatientSummary).filter((item: PatientSummary | null) => item != null)

        return patients;
    } catch (error) {
        console.log(error);
        throw error as Error;
    }
}

/** 
 * Gets a patient by their ID
 * @param authToken - Bearer token for authentication
 * @param patientId - The id of the patient to retrieve
 * @param clientId - The id for the client partition
 * @returns Promise resolving to a Patient object or null
 */
const getPatient = async (
    authToken: string,
    patientId: string,
    clientId?: string | undefined,
): Promise<Patient | null> => {

    if (patientId.length == 0) {
        return null;
    }

    if (!authToken) {
        console.error("getPatient No token!")
        return null
    }

    const url = getPatientURL(clientId, patientId)

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 404) {
            console.warn("Site not found");
            return null;
        }

        if (!response.ok) {
            console.error("Network response was not ok")
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        if (data?.patientId) {
            return dataToPatient(data);
        }

        const patients: Patient[] = data.map(dataToPatient).filter((item: Patient | null) => item != null);

        if (patients.length == 0) {
            return null
        }

        return patients[0]

    } catch (error: unknown) {
        console.log("get patient", error, patientId, clientId, authToken.length);
        throw error as Error;
    }
}

/** 
 * Adds a new patient
 * @param authToken - Bearer token for authentication
 * @param clientId - The id for the client partition
 * @returns Promise resolving to the newly created Patient object
 */
const addPatient = async (
    authToken: string,
    clientId?: string | undefined,
): Promise<Patient | null> => {

    const url = getPatientURL(clientId)

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        return dataToPatient(data);
    } catch (error) {
        console.log(error);
        throw error as Error;
    }
}

/** 
 * Updates the status of an existing patient
 * @param authToken - Bearer token for authentication
 * @param patientId - The ID of the patient to update
 * @param status - The new status for the patient
 * @param clientId - The id for the client partition
 * @returns Promise resolving to the updated Patient object
 */
const updatePatientStatus = async (
    authToken: string,
    patientId: string,
    status: ActiveStatus,
    clientId?: string | undefined,
): Promise<Patient | null> => {

    const url = getPatientURL(clientId, patientId)

    const body = {
        status,
    }

    try {
        const response = await fetch(url, {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        return dataToPatient(data);
    } catch (error) {
        console.log(error);
        throw error as Error;
    }
}

/**
 * Fetches multiple patients by their IDs
 * @param authToken - Bearer token for authentication
 * @param patientIds - Array of patient IDs to fetch
 * @param clientId - The id for the client partition
 * @returns Promise resolving to an array of Patient objects
 */
const getPatientsWithIds = async (
    authToken: string,
    patientIds: string[],
    clientId?: string | undefined,
): Promise<(Patient | null)[]> => {

    if (!authToken) {
        console.error("getPatientsWithIds No token!")
        return []
    }

    return batchFetchItems(
        authToken,
        patientIds,
        getPatientURL,
        dataToPatient,
        clientId,
    )
}

const PatientService = {
    addPatient,
    listPatients,
    getPatient,
    getPatientsWithIds,
    updatePatientStatus,
};

export default PatientService;